/**
 * Feature flags — the client half of `backend/feature_flags.py`.
 *
 * GET /api/features → { "WORKSPACE_CREATION": false }
 *
 * ── Why this is cached GLOBALLY and not with wsKey() ────────────────────────
 * The backend resolves flags from the process environment (`LM_FEATURE_<NAME>`),
 * with no reference to the caller's user or workspace. The response is therefore
 * identical for every tenant, and caching it across a workspace switch cannot
 * leak anything. That is a property of the current endpoint, NOT a general
 * licence: the day a flag becomes per-workspace (per-plan rollout, a beta cohort,
 * anything keyed on who is asking) this cache serves workspace A's answer to
 * workspace B, and the fix is to move it to react-query under
 * `wsKey(workspaceId, "features")` — see lib/queryKeys.js. If you are editing
 * the backend to make flags tenant-aware, you are also editing this file.
 *
 * ── Fail closed ─────────────────────────────────────────────────────────────
 * Unknown flag, request in flight, request failed → `false`. A flag is a
 * capability; the cost of hiding a real feature for one page load is a moment of
 * absence, and the cost of showing a fake one is a user clicking into a 403. The
 * consumer contract is "hide, don't disable" (a disabled control advertises a
 * feature that does not exist), so `false` renders as nothing at all and the
 * `loaded` bit exists for callers that need to tell "off" from "not yet known".
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export const FLAG = {
  WORKSPACE_CREATION: "WORKSPACE_CREATION",
};

// Module-level singleton: one request per page load, shared by every consumer.
// The full-page reload on workspace switch (WorkspaceSwitcher) discards this,
// and so does Phase 5's soft switch — because flags are not workspace-scoped,
// neither one needs to.
let inflight = null;
let resolved = null;

export function fetchFeatures() {
  if (resolved) return Promise.resolve(resolved);
  if (inflight) return inflight;
  inflight = api
    .get("/features")
    .then((r) => {
      resolved = (r.data && typeof r.data === "object") ? r.data : {};
      return resolved;
    })
    .catch((e) => {
      // Never the server's words on a screen, and never a capability granted by
      // a failed request. Log for us, `{}` (everything off) for the UI.
      // eslint-disable-next-line no-console
      console.error("[features] fetch failed", e?.response?.status);
      resolved = {};
      return resolved;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Test/instrumentation only — nothing in the app should call this. */
export function __resetFeatures() {
  inflight = null;
  resolved = null;
}

/**
 * `const { enabled, loaded } = useFeatureFlag(FLAG.WORKSPACE_CREATION)`
 *
 * `enabled` is false until proven true. `loaded` distinguishes "we asked and it
 * is off" from "we have not asked yet" for callers that want to hold a slot.
 */
export function useFeatureFlag(name) {
  const [state, setState] = useState(() => ({
    enabled: resolved ? resolved[name] === true : false,
    loaded: resolved !== null,
  }));

  useEffect(() => {
    let cancelled = false;
    fetchFeatures().then((flags) => {
      if (cancelled) return;
      setState({ enabled: flags[name] === true, loaded: true });
    });
    return () => {
      cancelled = true;
    };
  }, [name]);

  return state;
}
